import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, Button, IconButton, Slider } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import DeleteIcon from '@mui/icons-material/Delete';
import { useDispatch } from 'react-redux';
import { setLoading } from '../../store/slices/common';
import { uploadImage } from '../../http/uploads';
import useStyles from './KaraokeStyles';

const ACCEPTED_TYPES = [
    'audio/mp3',
    'audio/mpeg',
    'audio/wav',
    'audio/x-wav',
    'audio/m4a',
    'audio/mp4',
    'audio/aac',
];

const VocalAudioUpload = ({
    vocalAudio,
    onUpload,
    onRemove,
    backingDuration,
    uploadHint = 'vocal preview',
    uploadedLabel = 'Vocal preview uploaded',
    metadataType = 'karaoke_vocal_preview',
    person,
    compact = false,
}) => {
    const classes = useStyles();
    const inputRef = useRef(null);
    const audioRef = useRef(null);
    const dispatch = useDispatch();
    const [isPlaying, setIsPlaying] = useState(false);
    const [durationWarning, setDurationWarning] = useState(null);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);

    useEffect(() => {
        setIsPlaying(false);
        setCurrentTime(0);
        setDuration(0);
    }, [vocalAudio]);

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const handleFileSelected = async (file) => {
        if (!file || !ACCEPTED_TYPES.includes(file.type)) {
            alert('Please upload a valid audio file (MP3, M4A, WAV, etc.)');
            return;
        }

        const localUrl = URL.createObjectURL(file);
        const tempAudio = new Audio(localUrl);

        tempAudio.onloadedmetadata = async () => {
            const vocalDuration = tempAudio.duration;
            if (backingDuration && Math.abs(vocalDuration - backingDuration) > 1) {
                setDurationWarning(
                    `Vocal preview (${formatTime(vocalDuration)}) differs from backing track (${formatTime(backingDuration)}). They should match for the best experience.`
                );
            } else {
                setDurationWarning(null);
            }

            const formData = new FormData();
            formData.append('file', file);
            formData.append('folder', 'karaoke');
            const metadata = { type: metadataType };
            if (person != null) metadata.person = person;
            formData.append('metadata', JSON.stringify(metadata));

            try {
                dispatch(setLoading(true));
                const res = await uploadImage(formData);
                const media = res?.data?.data;
                if (media?._id) {
                    onUpload({ _id: media._id, url: media.url || localUrl });
                }
            } catch (error) {
                console.error(error);
                alert('Failed to upload vocal preview. Please try again.');
            } finally {
                dispatch(setLoading(false));
                URL.revokeObjectURL(localUrl);
            }
        };
    };

    const handleInputChange = (e) => {
        const file = e.target.files?.[0];
        if (file) handleFileSelected(file);
        if (inputRef.current) inputRef.current.value = '';
    };

    const handlePlayPause = () => {
        if (!audioRef.current) return;
        if (isPlaying) {
            audioRef.current.pause();
            setIsPlaying(false);
        } else {
            audioRef.current.play();
            setIsPlaying(true);
        }
    };

    const handleRemove = () => {
        if (audioRef.current) {
            audioRef.current.pause();
        }
        setIsPlaying(false);
        setDurationWarning(null);
        onRemove();
    };

    const handleTimeUpdate = () => {
        if (!audioRef.current) return;
        setCurrentTime(audioRef.current.currentTime);
    };

    const handleLoadedMetadata = () => {
        if (!audioRef.current) return;
        setDuration(audioRef.current.duration || 0);
    };

    const handleSeek = (event, newValue) => {
        if (!audioRef.current) return;
        audioRef.current.currentTime = newValue;
        setCurrentTime(newValue);
    };

    if (vocalAudio) {
        return (
            <Box>
                <audio
                    ref={audioRef}
                    src={vocalAudio}
                    onEnded={() => setIsPlaying(false)}
                    onTimeUpdate={handleTimeUpdate}
                    onLoadedMetadata={handleLoadedMetadata}
                    style={{ display: 'none' }}
                />
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                    {uploadedLabel}
                </Typography>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        mt: 1,
                        backgroundColor: '#f5f5f5',
                        borderRadius: '18px',
                        padding: '2px 10px 2px 2px',
                        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
                    }}
                >
                    <IconButton
                        onClick={handlePlayPause}
                        size="small"
                        sx={{
                            backgroundColor: '#142C5C',
                            color: 'white',
                            padding: '8px',
                            '&:hover': { backgroundColor: '#0f2247', opacity: 0.9 },
                        }}
                    >
                        {isPlaying ? <PauseIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
                    </IconButton>
                    <Typography variant="body2" sx={{ margin: '0 10px', whiteSpace: 'nowrap' }}>
                        {formatTime(currentTime)} / {formatTime(duration)}
                    </Typography>
                    <Slider
                        value={Math.min(currentTime, duration || 0)}
                        min={0}
                        max={duration || 0}
                        onChange={handleSeek}
                        size="small"
                        sx={{
                            flexGrow: 1,
                            mx: 1,
                            color: '#142C5C',
                            '& .MuiSlider-thumb': {
                                height: '10px',
                                width: '10px',
                            },
                        }}
                    />
                    <IconButton onClick={handleRemove} size="small" color="error">
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                    <Button
                        size="small"
                        onClick={() => inputRef.current?.click()}
                        sx={{ color: '#142C5C', fontSize: '12px', flexShrink: 0 }}
                    >
                        Replace
                    </Button>
                </Box>
                {durationWarning && (
                    <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 1 }}>
                        {durationWarning}
                    </Typography>
                )}
                <input
                    ref={inputRef}
                    type="file"
                    accept=".mp3,.m4a,.wav,.aac"
                    onChange={handleInputChange}
                    style={{ display: 'none' }}
                />
            </Box>
        );
    }

    return (
        <Box>
            <Box
                className={classes.uploadBox}
                onClick={() => inputRef.current?.click()}
                sx={{ mt: compact ? 0 : 1, cursor: 'pointer', py: compact ? 1.5 : undefined }}
            >
                <Box display="flex" flexDirection="column" alignItems="center">
                    <CloudUploadIcon className={classes.uploadIcon} sx={compact ? { fontSize: 28 } : undefined} />
                    <Typography variant="body1" className={classes.instructions} sx={compact ? { fontSize: '13px' } : undefined}>
                        <strong>Click to upload</strong> {uploadHint}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={compact ? { fontSize: '11px' } : undefined}>
                        MP3, M4A, WAV (optional)
                    </Typography>
                </Box>
            </Box>
            <input
                ref={inputRef}
                type="file"
                accept=".mp3,.m4a,.wav,.aac"
                onChange={handleInputChange}
                style={{ display: 'none' }}
            />
        </Box>
    );
};

export default VocalAudioUpload;
